import { isInNextSixDays } from '@utils/product';
import { Trans, useTranslation } from 'next-i18next';
import { useMemo, useState } from 'react';

import {
  getProductRef,
  getProductStatus,
} from '../../../../helpers/purchasing';
import {
  ProductPurchaseDetails,
  ProductPurchasingController,
} from '../../../../types/Purchasing';
import { SimpleDialog } from '../../../Dialog/SimpleDialog';
import ProductDate from '../../ProductDate';
import { SignUpButton } from '../SignUpButton';
import { toClientUTCDateFormat } from '../utils';

type Props<T extends ProductPurchaseDetails> = {
  product: ProductType;
  purchasingController: ProductPurchasingController<T>;
  ranked: boolean;
};

export const BlockChainAuctionSubscriptionCta = ({
  product,
  purchasingController,
  ranked,
}: Props<ProductPurchaseDetails>) => {
  const { t } = useTranslation('product');
  const launchDate = new Date(toClientUTCDateFormat(product.launchDate));
  const [showDialog, setShowDialog] = useState(false);

  const paragraphs = t(
    ranked ? 'auction.ranked.learnMore.body' : 'auction.default.learnMore.body',
    {
      returnObjects: true,
    },
  ) as unknown as string[];

  const label = useMemo(() => {
    if (isInNextSixDays(launchDate)) {
      return ranked
        ? 'auction.ranked.subscriptionDate.countdown'
        : 'auction.default.subscriptionDate.countdown';
    }
    return ranked
      ? 'auction.ranked.subscriptionDate.date'
      : 'auction.default.subscriptionDate.date';
  }, [launchDate, ranked]);

  return (
    <div className="flex w-full flex-1 flex-col space-y-6">
      <div className="flex w-full flex-1 flex-col">
        <p className="text-center font-bold">{t(label)}</p>
        <p className="text-center">
          <ProductDate date={launchDate} />
        </p>
        <div className="mt-3 flex w-full flex-1 flex-col">
          <p className="text-center text-xs text-neutral-4">
            {t(ranked ? 'auction.ranked.help' : 'auction.default.help')}
          </p>
        </div>
      </div>

      <div className="flex w-full flex-1 flex-col items-center space-y-3">
        <SignUpButton
          labels={{ normal: t('notifyMe'), success: t('subscribed') }}
          product={product}
          productRef={getProductRef(purchasingController)}
          productStatus={getProductStatus(product, purchasingController)}
          showIcon
        />
        <button
          className="hyperlink"
          onClick={() => {
            setShowDialog(true);
          }}
          type="button"
        >
          {t(
            ranked
              ? 'auction.ranked.learnMore.title'
              : 'auction.default.learnMore.title',
          )}
        </button>
        <SimpleDialog
          onClose={() => setShowDialog(false)}
          open={showDialog}
          title={t(
            ranked
              ? 'auction.ranked.learnMore.title'
              : 'auction.default.learnMore.blockChain.title',
          )}
        >
          <div className="flex flex-col gap-3">
            {paragraphs.map((paragraph, index) => (
              <p>
                <Trans
                  key={paragraph}
                  components={{
                    faq: (
                      <a
                        className="hyperlink"
                        href="https://avantarte.com/support/shipping#will-i-be-charged-duties-and-taxes-by-the-customs-of-my-country"
                        rel="noreferrer"
                        target="_blank"
                      >
                        FAQs
                      </a>
                    ),
                  }}
                  i18nKey={
                    ranked
                      ? `auction.ranked.learnMore.body.${index}`
                      : `auction.default.learnMore.blockChain.body.${index}`
                  }
                >
                  {paragraph}
                </Trans>
              </p>
            ))}
          </div>
        </SimpleDialog>
      </div>
    </div>
  );
};
